import React, { useState, useEffect } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { apiService } from '../../services/api';
import PayoutManager from './PayoutManager';

const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  } else if (hostname === '10.0.0.28') {
    return `https://${hostname}/api`;
  } else {
    return `https://${hostname}/api`;
  }
};

const TournamentControl = ({ tournamentId }) => {
  const { connected, tournamentData, joinTournament } = useSocket();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockData, setClockData] = useState(null);
  const [localTimeRemaining, setLocalTimeRemaining] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [customTime, setCustomTime] = useState('');
  const [manualTimeMinutes, setManualTimeMinutes] = useState('');
  const [manualTimeSeconds, setManualTimeSeconds] = useState('');
  const [tournamentStructure, setTournamentStructure] = useState([]);
  const [showPayouts, setShowPayouts] = useState(false);
  const [showPayoutManager, setShowPayoutManager] = useState(false);

  useEffect(() => {
    loadTournamentData();
    loadClockData();
    loadTournamentStructure();
  }, [tournamentId]);

  // Join tournament room for real-time updates
  useEffect(() => {
    if (tournamentId && connected) {
      joinTournament(tournamentId);
    }
  }, [tournamentId, connected, joinTournament]);

  // Update local data when WebSocket data changes
  useEffect(() => {
    if (tournamentData) {
      setClockData(tournamentData);
      setLocalTimeRemaining(tournamentData.time_remaining_seconds);
      // Also update tournament data
      setTournament(prev => ({
        ...prev,
        status: tournamentData.status,
        current_level: tournamentData.current_level,
        time_remaining_seconds: tournamentData.time_remaining_seconds,
        players_remaining: tournamentData.players_remaining
      }));
    }
  }, [tournamentData]);

  useEffect(() => {
    if (clockData) {
      setLocalTimeRemaining(clockData.time_remaining_seconds);
    }
  }, [clockData]);

  // Synchronized countdown timer effect
  useEffect(() => {
    if (!tournamentData) {
      return;
    }

    // Always set the initial time from server data
    setLocalTimeRemaining(tournamentData.time_remaining_seconds);

    // Only start countdown if tournament is running OR on break
    if (tournamentData.status !== 'running' && tournamentData.status !== 'break') {
      return;
    }

    // Start countdown from the server time (works for both running and break status)
    let currentTime = tournamentData.time_remaining_seconds;

    const timer = setInterval(() => {
      currentTime -= 1;
      if (currentTime <= 0) {
        setLocalTimeRemaining(0);
        clearInterval(timer);
      } else {
        setLocalTimeRemaining(currentTime);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [tournamentData?.time_remaining_seconds, tournamentData?.status, tournamentData?.current_level]);

  const loadTournamentData = async () => {
    try {
      const response = await apiService.getTournament(tournamentId);
      setTournament(response.data);
      setShowPayouts(tournamentData.show_payouts || false);
      setLoading(false);
    } catch (error) {
      console.error('Error loading tournament:', error);
      setLoading(false);
    }
  };

  const loadClockData = async () => {
    try {
      const response = await apiService.getTournamentClock(tournamentId);
      setClockData(response.data);
    } catch (error) {
      console.error('Error loading clock data:', error);
    }
  };

  const loadTournamentStructure = async () => {
    try {
      console.log('Loading tournament structure for:', tournamentId);
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/structure`);
      const data = await response.json();
      console.log('Structure data:', data);
      setTournamentStructure(data.structure || []);
    } catch (error) {
      console.error('Error loading tournament structure:', error);
      setTournamentStructure([]);
    }
  };

  const handleStartTournament = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        // Give the WebSocket a moment to update, then refresh if needed
        setTimeout(() => {
          loadTournamentData();
          loadClockData();
        }, 500);
      }
    } catch (error) {
      console.error('Error starting tournament:', error);
    }
  };

  const togglePayouts = async () => {
  try {
    const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        show_payouts: !showPayouts
      })
    });

    if (response.ok) {
      setShowPayouts(!showPayouts);
    }
  } catch (error) {
    console.error('Error toggling payouts:', error);
  }
};

  const handlePauseTournament = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/pause`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          current_time_remaining: localTimeRemaining
        })
      });
      if (response.ok) {
        setTimeout(() => {
          loadTournamentData();
          loadClockData();
        }, 500);
      }
    } catch (error) {
      console.error('Error pausing tournament:', error);
    }
  };

  const handleNextLevel = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/next-level`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (response.ok) {
        setTimeout(() => {
          loadTournamentData();
          loadClockData();
        }, 500);
      }
    } catch (error) {
      console.error('Error advancing level:', error);
    }
  };

  const handleJumpToLevel = async () => {
    if (!selectedLevel) return;
    
    console.log(`Jumping to level ${selectedLevel}`);
    console.log(`Custom time: ${customTime} minutes`);
    
    // Find the level info from tournament structure
    const levelInfo = tournamentStructure.find(level => level.level === parseInt(selectedLevel));
    console.log('Level info:', levelInfo);
    
    let timeInSeconds;
    if (customTime) {
      timeInSeconds = parseInt(customTime) * 60;
    } else if (levelInfo) {
      timeInSeconds = levelInfo.duration * 60;
    } else {
      timeInSeconds = 1200; // Default 20 minutes
    }
    
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          current_level: parseInt(selectedLevel),
          time_remaining_seconds: timeInSeconds
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Jump result:', result);
        
        // Clear the form
        setSelectedLevel('');
        setCustomTime('');
        
        setTimeout(() => {
          loadTournamentData();
          loadClockData();
        }, 500);
      }
    } catch (error) {
      console.error('Error jumping to level:', error);
    }
  };

  const handleManualTimeOverride = async () => {
    if (!manualTimeMinutes && !manualTimeSeconds) return;
    
    const minutes = parseInt(manualTimeMinutes) || 0;
    const seconds = parseInt(manualTimeSeconds) || 0;
    const totalSeconds = (minutes * 60) + seconds;
    
    try {
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          time_remaining_seconds: totalSeconds
        })
      });
      if (response.ok) {
        setManualTimeMinutes('');
        setManualTimeSeconds('');
        setTimeout(() => {
          loadTournamentData();
          loadClockData();
        }, 500);
      }
    } catch (error) {
      console.error('Error overriding time:', error);
    }
  };

  const startBreak = async (minutes) => {
    try {
      console.log('=== STARTING BREAK ===');
      console.log(`Break duration: ${minutes} minutes`);
      console.log(`localTimeRemaining:`, localTimeRemaining);
      console.log(`tournament status:`, tournament?.status);
      console.log(`tournamentData:`, tournamentData);
      
      const timeToSend = localTimeRemaining !== null ? localTimeRemaining : tournament?.time_remaining_seconds;
      console.log(`Time to send to server:`, timeToSend);
      
      const requestBody = {
        break_duration_minutes: minutes,
        current_time_remaining: timeToSend
      };
      console.log('Request body:', requestBody);
      
      const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/start-break`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(requestBody)
      });
      
      const responseText = await response.text();
      console.log('Server response:', responseText);
      
      if (response.ok) {
        setTimeout(() => {
          loadTournamentData();
          loadClockData();
        }, 500);
      }
    } catch (error) {
      console.error('Error starting break:', error);
    }
  };

  // Helper function to format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to format blinds
  const formatBlinds = (blinds) => {
    if (!blinds) return 'N/A';
    if (blinds.type === 'break') return 'BREAK';
    const ante = blinds.ante > 0 ? ` (${blinds.ante})` : '';
    return `${blinds.small_blind}/${blinds.big_blind}${ante}`;
  };

  if (loading) return <div className="container">Loading tournament control...</div>;
  if (!tournament) return <div className="container">Tournament not found</div>;

  return (
    <div className="container" style={{ maxWidth: '1200px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Tournament Control Panel</h1>
        <button 
          className="btn"
          onClick={() => window.location.href = '/admin/tournaments'}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Tournament Info Header */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h2>{clockData?.tournament_name || 'Tournament'}</h2>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <strong>Status:</strong> 
            <span style={{ 
              marginLeft: '10px',
              padding: '4px 12px',
              borderRadius: '4px',
              background: tournament.status === 'running' ? '#d4edda' : 
                         tournament.status === 'paused' ? '#fff3cd' : 
                         tournament.status === 'break' ? '#d1ecf1' :
                         tournament.status === 'registration' ? '#cce5ff' : '#f8d7da',
              color: tournament.status === 'running' ? '#155724' : 
                     tournament.status === 'paused' ? '#856404' : 
                     tournament.status === 'break' ? '#0c5460' :
                     tournament.status === 'registration' ? '#004085' : '#721c24',
              fontWeight: 'bold'
            }}>
              {tournament.status?.toUpperCase()}
            </span>
            {connected ? ' 🟢' : ' 🔴'}
          </div>
          <div><strong>Dealer Code:</strong> {tournament.dealer_code}</div>
        </div>
      </div>

      {/* Quick Stats Grid - CORRECTED TO USE clockData */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '20px' }}>
        {/* 1. Current Level */}
        <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Current Level</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2196F3' }}>
            {tournament.status === 'break' ? 'BREAK' : (clockData?.current_level || tournament.current_level)}
          </div>
        </div>

        {/* 2. Time Remaining */}
        <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Time Remaining</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#FF9800' }}>
            {formatTime(localTimeRemaining !== null ? localTimeRemaining : tournament.time_remaining_seconds)}
          </div>
        </div>

        {/* 3. Current Blinds */}
        <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Current Blinds</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#F44336' }}>
            {clockData?.current_blinds ? formatBlinds(clockData.current_blinds) : 'N/A'}
          </div>
        </div>

        {/* 4. Next Blinds */}
        <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Next Blinds</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#9E9E9E' }}>
            {clockData?.next_blinds ? formatBlinds(clockData.next_blinds) : 'N/A'}
          </div>
        </div>

        {/* 5. Players Remaining */}
        <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Players Remaining</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#4CAF50' }}>
            {clockData?.players_remaining || tournament.players_remaining}
          </div>
        </div>

        {/* 6. Players Entered */}
        <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Players Entered</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2196F3' }}>
            {clockData?.total_entries || 0}
          </div>
        </div>

        {/* 7. Rebuys */}
        <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Rebuys</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#9C27B0' }}>
            {clockData?.total_rebuys || 0}
          </div>
        </div>

        {/* 8. Prize Pool */}
        <div className="card" style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>Prize Pool</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#4CAF50' }}>
            ${clockData?.total_prize_pool || 0}
          </div>
        </div>
      </div>

      {/* Clock Controls */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3>Clock Controls</h3>
        {/* Timer display */}
        <div style={{ 
          textAlign: 'center', 
          fontSize: '48px', 
          fontWeight: 'bold', 
          margin: '20px 0',
          padding: '20px',
          background: tournament?.status === 'running' ? '#d4edda' : '#f8d7da',
          borderRadius: '8px',
          color: tournament?.status === 'running' ? '#155724' : '#721c24'
        }}>
          {formatTime(localTimeRemaining !== null ? localTimeRemaining : tournament.time_remaining_seconds)}
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '20px' }}>
          <button 
            className="btn btn-success" 
            onClick={handleStartTournament}
            disabled={tournament.status === 'running'}
          >
            ▶ START
          </button>
          <button 
            className="btn btn-warning" 
            onClick={handlePauseTournament}
            disabled={tournament.status === 'paused'}
          >
            ⏸ PAUSE
          </button>
          <button 
            className="btn" 
            onClick={handleNextLevel}
          >
            ⏭ FORCE NEXT LEVEL
          </button>
        </div>

        {tournament.status === 'break' && (
          <div style={{ marginBottom: '20px' }}>
            <button 
              className="btn btn-success" 
              onClick={async () => {
                const response = await fetch(`${getApiUrl()}/tournaments/${tournamentId}/end-break`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
                });
                if (response.ok) {
                  setTimeout(() => {
                    loadTournamentData();
                    loadClockData();
                  }, 500);
                }
              }}
            >
              ✓ END BREAK
            </button>
          </div>
        )}

        <button
          className="btn"
          onClick={() => setShowPayoutManager(true)}
          style={{ backgroundColor: '#6f42c1', color: 'white' }}
        >
          Configure Payouts
        </button>

        <button
          className="btn"
          onClick={togglePayouts}
          style={{
            backgroundColor: showPayouts ? '#28a745' : '#6c757d',
            color: 'white'
          }}
        >
          {showPayouts ? '✓ Payouts Visible' : 'Show Payouts'}
        </button>
        
        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Start Break:</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginBottom: '20px' }}>
          <button className="btn btn-warning" onClick={() => startBreak(5)}>5 min</button>
          <button className="btn btn-warning" onClick={() => startBreak(10)}>10 min</button>
          <button className="btn btn-warning" onClick={() => startBreak(15)}>15 min</button>
          <button className="btn btn-warning" onClick={() => startBreak(20)}>20 min</button>
        </div>

        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Jump to Level:</h4>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '20px' }}>
          <div className="form-group" style={{ margin: 0, flex: 1, minWidth: '200px' }}>
            <select 
              value={selectedLevel} 
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="form-control"
            >
              <option value="">Select Level</option>
              {console.log('Tournament structure:', tournamentStructure)}
              {tournamentStructure && tournamentStructure.length > 0 ? (
                tournamentStructure.map(levelInfo => (
                  <option key={levelInfo.level} value={levelInfo.level}>
                    Level {levelInfo.level} {levelInfo.type === 'break' ? '(Break)' : `(${levelInfo.small_blind}/${levelInfo.big_blind})`}
                  </option>
                ))
              ) : (
                <option disabled>No structure loaded</option>
              )}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <input 
              type="number"
              placeholder="Custom time (min)"
              value={customTime}
              onChange={(e) => setCustomTime(e.target.value)}
              className="form-control"
              style={{ width: '150px' }}
            />
          </div>
          <button className="btn btn-primary" onClick={handleJumpToLevel}>Jump to Level</button>
        </div>

        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Manual Time Override:</h4>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <input 
              type="number"
              placeholder="Minutes"
              value={manualTimeMinutes}
              onChange={(e) => setManualTimeMinutes(e.target.value)}
              className="form-control"
              style={{ width: '100px' }}
            />
          </div>
          <span style={{ fontSize: '20px', fontWeight: 'bold' }}>:</span>
          <div className="form-group" style={{ margin: 0 }}>
            <input 
              type="number"
              placeholder="Seconds"
              value={manualTimeSeconds}
              onChange={(e) => setManualTimeSeconds(e.target.value)}
              className="form-control"
              style={{ width: '100px' }}
              min="0"
              max="59"
            />
          </div>
          <button className="btn btn-primary" onClick={handleManualTimeOverride}>Override Time</button>
        </div>
      </div>

      {/* Quick Access */}
      <div className="card">
        <h3>Quick Access</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
          <button 
            className="btn"
            onClick={() => window.open(`/clock/${tournamentId}`, '_blank')}
          >
            🖥 Open Clock Display
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => window.location.href = `/admin/tournament/${tournamentId}/settings`}
          >
            ⚙️ Clock Display Settings
          </button>
          <button 
            className="btn btn-success"
            onClick={() => window.location.href = `/admin/tournament/${tournamentId}/registrations`}
          >
            👥 Player Registration
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => window.location.href = `/admin/tournament/${tournamentId}/tables`}
          >
            🎲 Table Management
          </button>
        </div>

        {showPayoutManager && (
            <PayoutManager
              tournamentId={tournamentId}
              onClose={() => {
                setShowPayoutManager(false);
                loadTournamentData();
              }}
            />
          )}
      </div>
    </div>
  );
};

export default TournamentControl;